import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import type { LucideIcon } from "lucide-react";
import {
  ChevronDown,
  ChevronUp,
  Menu as MenuIcon,
  PanelLeft,
  Plus,
  Search,
  Star,
  UserRound,
  X,
} from "lucide-react";

import { cn } from "@/lib/cn";
import { IconButton } from "@/components/ui/IconButton";
import { buttonClasses } from "@/components/ui/Button";
import { Menu, MenuItem, MenuLabel } from "@/components/ui/Menu";
import { Tooltip } from "@/components/ui/Tooltip";
import { Wordmark } from "@/components/layout/Wordmark";
import { useUnreadMessages } from "@/features/chat/hooks";
import { useNavCounts, type NavCounts } from "@/features/dashboard/hooks";
import { linkFor, navItemAt, type NavGroup, type NavItem } from "@/app/nav";
import { CommandPalette } from "@/app/layouts/CommandPalette";
import { ShortcutSheet } from "@/app/layouts/ShortcutSheet";
import type { CreateAction } from "@/app/create";
import { askToCreate } from "@/lib/create";
import { NotificationBell } from "@/features/notifications/NotificationBell";
import { canViewAdmin, isReadOnly } from "@/lib/auth/access";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useFavourites, type Favourite } from "@/lib/favourites";
import { onMac, showShortcuts, useShortcuts, type Shortcuts } from "@/lib/shortcuts";

export interface AppShellProps {
  nav: NavGroup[];
  /** What the New button offers. No button without any. */
  create?: CreateAction[];
  /** Rendered at the top right: notifications, account menu. */
  utility?: ReactNode;
  /** Pinned to the bottom of the sidebar, below the nav. */
  footer?: ReactNode;
}

const SIDEBAR_KEY = "hrsys.shell.sidebar";
const SECTIONS_KEY = "hrsys.shell.sections";
/** The heading over what somebody pinned. It folds like any other. */
const FAVOURITES = "Favourites";

function readStored<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function store(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage may be blocked or full; the shell simply forgets on reload.
  }
}

/** What is waiting behind an item: its queue, or the unread messages. */
function waitingOn(item: NavItem, counts: NavCounts | undefined, unread: number): number {
  if (item.to.endsWith("/chat")) return unread;
  return item.countKey ? (counts?.[item.countKey] ?? 0) : 0;
}

/**
 * The frame around every signed-in page.
 *
 * Laid out as a frame with panels in it: a bar across the top with the
 * name, the search and the account, and two white panels standing on the
 * grey frame — the sidebar, with every screen under its heading, and the
 * page. There is one navigation. It folds to a strip of icons, which gives
 * the rota the whole window without taking away the way to move. What
 * somebody pins goes at the top of that same list, under a heading of its
 * own: a second strip of pinned places stood beside it for a day and said
 * the same things twice.
 *
 * The keyboard has the same ways in: "[" folds the sidebar, G and a letter
 * goes to a screen, and "?" lists every key there is.
 *
 * Narrow screens drop the frame: the page is the screen, the sidebar is a
 * drawer, and the most-used places are a tab bar along the bottom, because
 * employees use this on a phone while standing at a counter.
 */
export function AppShell({ nav, create = [], utility, footer }: AppShellProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const { session } = useAuth();
  const isManager = session?.is_manager ?? false;
  const supervises = session?.supervises ?? false;
  const inPayrollChain = session?.in_payroll_chain ?? false;
  const readOnly = isReadOnly(session);
  // An approver reads HR's screens and approves, but changes nothing: on
  // those screens the shell says so, and offers nothing to start.
  const readingHr = readOnly && location.pathname.startsWith("/admin");
  const actions = readingHr ? [] : create;
  const visible = useMemo(
    () =>
      nav
        .map((group) => ({
          ...group,
          items: group.items.filter(
            (item) =>
              (!item.managerOnly || isManager) &&
              (!item.supervisorOnly || supervises) &&
              (!item.payrollChainOnly || inPayrollChain) &&
              (!item.approverOnly || readOnly),
          ),
        }))
        .filter((group) => group.items.length > 0),
    [nav, isManager, supervises, inPayrollChain, readOnly],
  );
  const tabs = visible.flatMap((group) => group.items).filter((item) => item.onTabBar);
  const { data: messages } = useUnreadMessages();
  const unread = messages?.unread ?? 0;
  const isAdmin = session?.user.role === "admin";
  const seesHr = canViewAdmin(session);
  const { data: counts } = useNavCounts(
    session ? (isAdmin || readingHr ? "admin" : "me") : null,
  );
  const [searching, setSearching] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => readStored(SIDEBAR_KEY, true));
  const navigate = useNavigate();

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((open) => {
      store(SIDEBAR_KEY, !open);
      return !open;
    });
  }, []);

  // The letters after G come from the list the sidebar is drawn from, so a
  // key only ever goes where the signed-in person is allowed to.
  const shortcuts = useMemo<Shortcuts>(() => {
    const go: Shortcuts["go"] = {};
    for (const group of visible) {
      for (const item of group.items) {
        if (!item.goKey) continue;
        go[item.goKey] = () => {
          setDrawerOpen(false);
          navigate(item.to);
        };
      }
    }
    return { keys: { "?": () => showShortcuts(), "[": toggleSidebar }, go };
  }, [visible, navigate, toggleSidebar]);
  useShortcuts(shortcuts);

  return (
    <div className="relative min-h-dvh lg:flex lg:h-dvh lg:flex-col lg:overflow-hidden">
      {/* The first thing a keyboard reaches. Seventeen sidebar items stand
          between Tab and the page otherwise. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-sm focus:bg-surface focus:px-4 focus:py-2 focus:text-base focus:font-semibold focus:text-fg focus:shadow-pop"
      >
        Skip to content
      </a>

      {/* The bar across the top of the frame. It sits straight on the grey:
          the name on the left, the search in the middle where a hand expects
          it, the account on the right. */}
      <header className="hidden h-12 shrink-0 items-center gap-3 px-3 lg:flex">
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <IconButton
            label={sidebarOpen ? "Fold the sidebar" : "Open the sidebar"}
            icon={<PanelLeft className="size-4" />}
            size="sm"
            aria-expanded={sidebarOpen}
            aria-controls="shell-sidebar"
            onClick={toggleSidebar}
            className="hover:bg-ink-900/[0.07] active:bg-ink-900/10"
          />
          <Wordmark compact />
        </div>
        <SearchButton onClick={() => setSearching(true)} />
        <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
          <NewButton actions={actions} />
          <NotificationBell />
          {utility}
        </div>
      </header>

      <div className="lg:flex lg:min-h-0 lg:flex-1 lg:gap-2 lg:px-2 lg:pb-2">
        {/* The sidebar: every screen, under the question it answers. Folded,
            it is the same list as icons, each named on hover. */}
        <aside
          id="shell-sidebar"
          className={cn(
            "hidden shrink-0 flex-col rounded-lg border border-line bg-rail transition-[width] duration-150 lg:flex",
            sidebarOpen ? "w-rail" : "w-13",
          )}
        >
          <NavList
            nav={visible}
            onRail
            folded={!sidebarOpen}
            unread={unread}
            counts={counts}
            className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-2"
          />
          {footer && sidebarOpen ? <div className="mt-auto px-2 pb-2 pt-1">{footer}</div> : null}
        </aside>

        {/* Mobile drawer */}
        {drawerOpen ? (
          <div className="fixed inset-0 z-50 lg:hidden">
            <button
              type="button"
              aria-label="Close menu"
              className="absolute inset-0 bg-scrim/45"
              onClick={() => setDrawerOpen(false)}
            />
            <aside className="absolute inset-y-0 left-0 flex w-72 max-w-[calc(100vw-3rem)] flex-col bg-surface shadow-pop">
              <div className="flex items-center justify-between gap-2 border-b border-line-faint px-4 py-3">
                <Wordmark />
                <IconButton
                  tipless
                  label="Close menu"
                  icon={<X className="size-5" />}
                  onClick={() => setDrawerOpen(false)}
                />
              </div>
              <NavList
                nav={visible}
                unread={unread}
                counts={counts}
                className="min-h-0 flex-1 overflow-y-auto p-2"
                onNavigate={() => setDrawerOpen(false)}
              />
              {footer ? <div className="mt-auto px-2 pb-3 pt-1">{footer}</div> : null}
            </aside>
          </div>
        ) : null}

        {/* The page: a white panel on the frame on a desktop, the whole
            screen on a phone. */}
        <div
          data-shell="page"
          className="flex min-w-0 flex-1 flex-col bg-surface max-lg:min-h-dvh lg:overflow-hidden lg:rounded-lg lg:border lg:border-line"
        >
          {/* A phone's own bar: the drawer, the name, the search, the
              account. A desktop has the frame's bar instead. */}
          <header className="sticky top-0 z-20 flex h-13 shrink-0 items-center justify-between gap-3 border-b border-line bg-surface px-3 lg:hidden">
            <div className="flex min-w-0 items-center gap-1.5">
              <IconButton
                label="Open menu"
                icon={<MenuIcon className="size-5" />}
                onClick={() => setDrawerOpen(true)}
              />
              <span className="u-display truncate text-md font-bold text-fg">
                First Gulf Exchange
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <IconButton
                label="Search (⌘K)"
                icon={<Search className="size-5" />}
                onClick={() => setSearching(true)}
              />
              <NewButton actions={actions} compact />
              <NotificationBell />
              {utility}
            </div>
          </header>

          {/* Positioned, so that anything absolutely placed inside the page —
              a visually hidden label, a live region — is measured against
              this scroller and scrolls with it. Left unpositioned, such an
              element far down a long page is laid out against the window
              instead, escapes this box's clipping, and makes the whole
              frame scroll away into empty grey. */}
          <div className="relative lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
            {readingHr ? (
              <p
                role="note"
                className="border-b border-line-faint bg-surface-sunken px-4 py-1.5 text-sm text-fg-muted sm:px-6"
              >
                Read-only: you can approve, not change.
              </p>
            ) : null}
            <main
              id="main"
              tabIndex={-1}
              key={location.pathname}
              className="mx-auto w-full max-w-[82rem] px-4 pb-24 pt-5 outline-none sm:px-6 lg:pb-10 lg:pt-6"
            >
              <Outlet />
            </main>
          </div>
        </div>
      </div>

      <CommandPalette
        nav={visible}
        create={actions}
        canSearchPeople={seesHr}
        open={searching}
        onOpenChange={setSearching}
      />

      <ShortcutSheet nav={visible} canSearchPeople={seesHr} />

      {/* Mobile tab bar: along the bottom edge, the way a phone's own apps
          put theirs. */}
      {tabs.length > 0 ? (
        <nav
          aria-label="Main"
          className="fixed inset-x-0 bottom-0 z-30 grid border-t border-line bg-surface px-1 pb-[env(safe-area-inset-bottom)] lg:hidden"
          style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
        >
          {tabs.map((item) => (
            <NavLink
              key={item.to}
              to={linkFor(item, waitingOn(item, counts, unread))}
              end={item.to === "/admin"}
              className={({ isActive }) =>
                cn(
                  "flex min-w-0 flex-col items-center gap-0.5 px-1 pb-1.5 pt-2 text-2xs",
                  isActive ? "font-semibold text-accent" : "font-medium text-fg-subtle",
                )
              }
            >
              {({ isActive }) => {
                const waiting = waitingOn(item, counts, unread);
                return (
                  <>
                    <span className="relative">
                      <item.icon
                        className={cn("size-5", isActive && "text-accent")}
                        strokeWidth={isActive ? 2.25 : 1.75}
                        aria-hidden="true"
                      />
                      {waiting > 0 ? (
                        <span
                          className="absolute -right-2 -top-1.5 grid min-w-4 place-items-center rounded-full bg-accent px-1 text-[0.625rem] font-bold leading-4 text-white"
                          aria-label={`${waiting} waiting`}
                        >
                          {waiting > 99 ? "99+" : waiting}
                        </span>
                      ) : null}
                    </span>
                    <span className="max-w-full truncate">{item.label}</span>
                  </>
                );
              }}
            </NavLink>
          ))}
        </nav>
      ) : null}
    </div>
  );
}

/**
 * New: one button for whatever somebody wants to start, wherever they are.
 * The thing chosen opens on the screen it belongs to.
 */
function NewButton({ actions, compact = false }: { actions: CreateAction[]; compact?: boolean }) {
  const navigate = useNavigate();
  if (actions.length === 0) return null;
  return (
    <Menu
      align="end"
      trigger={
        compact ? (
          <IconButton label="New" icon={<Plus className="size-5" />} />
        ) : (
          <button type="button" data-print="hide" className={buttonClasses("primary", "sm")}>
            <Plus className="size-4" aria-hidden="true" />
            New
          </button>
        )
      }
    >
      <MenuLabel>Start a new</MenuLabel>
      {actions.map((action) => (
        <MenuItem
          key={action.label}
          icon={<action.icon className="size-4" />}
          onSelect={() => {
            if (action.ask) askToCreate(action.ask);
            navigate(action.to);
          }}
        >
          {action.label}
        </MenuItem>
      ))}
    </Menu>
  );
}

/**
 * The way into the search box on a desktop: looks like a field, opens the
 * palette. It sits in the middle of the top bar, where a hand looks for it,
 * and the shortcut is printed on it, because a shortcut nobody is told about
 * is a shortcut nobody uses.
 */
function SearchButton({ onClick }: { onClick: () => void }) {
  const mac = onMac();
  return (
    <button
      type="button"
      onClick={onClick}
      data-print="hide"
      className={cn(
        "flex h-8 w-full max-w-md items-center gap-2 rounded-sm bg-ink-900/[0.06] px-2.5 text-left text-base text-fg-subtle",
        "transition-colors hover:bg-ink-900/[0.09] hover:text-fg-muted",
      )}
    >
      <Search className="size-4 shrink-0" aria-hidden="true" />
      <span className="flex-1 truncate">Search</span>
      <kbd className="rounded-xs bg-surface px-1.5 py-0.5 font-sans text-2xs font-medium text-fg-subtle">
        {mac ? "⌘K" : "Ctrl K"}
      </kbd>
    </button>
  );
}

function NavList({
  nav,
  className,
  onNavigate,
  onRail = false,
  folded: narrow = false,
  unread = 0,
  counts,
}: {
  nav: NavGroup[];
  className?: string;
  onNavigate?: () => void;
  /** The sidebar. The drawer is the same list on a phone, with taller rows. */
  onRail?: boolean;
  /** The sidebar folded to a strip: icons only, every section open. */
  folded?: boolean;
  /** Unread messages, shown against the one item that can carry them. */
  unread?: number;
  /** What is waiting behind each queue, by the item's countKey. */
  counts?: NavCounts;
}) {
  const location = useLocation();
  // Which headings are folded shut. A click always does what it says, even on
  // the section holding the current screen; arriving at a screen inside a
  // shut section opens it instead, so following a link still shows where
  // somebody has landed.
  const [folded, setFolded] = useState<string[]>(() => readStored<string[]>(SECTIONS_KEY, []));
  useEffect(() => store(SECTIONS_KEY, folded), [folded]);

  // Kept in state rather than an effect, so the section opens in the same
  // render that shows the new screen rather than flickering shut first.
  const [seenPath, setSeenPath] = useState<string | null>(null);
  if (seenPath !== location.pathname) {
    setSeenPath(location.pathname);
    const arrivedIn = nav.find((group) => holdsPath(group, location.pathname))?.heading;
    if (arrivedIn && folded.includes(arrivedIn)) {
      setFolded(folded.filter((one) => one !== arrivedIn));
    }
  }

  function toggle(heading: string) {
    setFolded((current) =>
      current.includes(heading)
        ? current.filter((one) => one !== heading)
        : [...current, heading],
    );
  }

  const { favourites, remove, move } = useFavourites();
  const favouritesOpen = narrow || !folded.includes(FAVOURITES);
  // A favourite is an address with its filter in it, so it is only "here"
  // when the whole address is.
  const address = `${location.pathname}${location.search}`;

  return (
    <nav aria-label="Main" className={className}>
      {/* What somebody pinned, above everything else, and only once there
          is something: an empty heading is a thing to wonder about. */}
      {favourites.length > 0 ? (
        <div>
          {narrow ? null : (
            <SectionHeading
              heading={FAVOURITES}
              open={favouritesOpen}
              onToggle={() => toggle(FAVOURITES)}
            />
          )}
          {favouritesOpen ? (
            <ul className="flex flex-col gap-px">
              {favourites.map((favourite, index) => (
                <FavouriteRow
                  key={favourite.to}
                  favourite={favourite}
                  icon={
                    navItemAt(nav, favourite.to)?.icon ??
                    (favourite.kind === "person" ? UserRound : Star)
                  }
                  here={address === favourite.to}
                  onRail={onRail}
                  narrow={narrow}
                  first={index === 0}
                  last={index === favourites.length - 1}
                  onNavigate={onNavigate}
                  onRemove={() => remove(favourite.to)}
                  onMove={(by) => move(favourite.to, by)}
                />
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {nav.map((group, index) => {
        const open = narrow || !group.heading || !folded.includes(group.heading);
        // A folded section still says something is waiting inside it.
        const waitingInside = group.items.reduce(
          (sum, item) => sum + waitingOn(item, counts, unread),
          0,
        );
        return (
          <div
            key={group.heading ?? index}
            className={cn(
              (index > 0 || favourites.length > 0) &&
                (narrow ? "mt-2 border-t border-line-faint pt-2" : "mt-3"),
            )}
          >
            {group.heading && !narrow ? (
              <SectionHeading
                heading={group.heading}
                open={open}
                waiting={waitingInside}
                onToggle={() => toggle(group.heading!)}
              />
            ) : null}
            {open ? (
              <ul className="flex flex-col gap-px">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={linkFor(item, waitingOn(item, counts, unread))}
                      end={item.to === "/admin"}
                      onClick={onNavigate}
                      // Folded, the icon is all there is, so the name has to
                      // be somewhere a pointer and a screen reader can find it.
                      title={narrow ? item.label : undefined}
                      aria-label={narrow ? item.label : undefined}
                      className={({ isActive }) =>
                        cn(
                          // Where you are is a pale blue row with blue
                          // text; everything else
                          // is grey until the pointer is on it.
                          "relative flex items-center gap-2.5 rounded-xs text-base transition-colors duration-100",
                          narrow ? "justify-center px-0" : "px-2",
                          onRail ? "h-8" : "h-10",
                          isActive
                            ? "bg-brand-50 font-medium text-brand-700"
                            : "text-fg-muted hover:bg-rail-hover hover:text-fg",
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <item.icon
                            className={cn("size-4 shrink-0", isActive ? "text-accent" : "text-fg-subtle")}
                            strokeWidth={isActive ? 2.25 : 1.75}
                            aria-hidden="true"
                          />
                          {narrow ? (
                            waitingOn(item, counts, unread) > 0 ? (
                              // No room for the figure; a dot says there is one.
                              <span
                                className={cn(
                                  "absolute right-1.5 top-1 size-2 rounded-full ring-2 ring-rail",
                                  item.to.endsWith("/chat") ? "bg-accent" : "bg-ochre-500",
                                )}
                                aria-label={`${waitingOn(item, counts, unread)} waiting`}
                              />
                            ) : null
                          ) : (
                            <span className="truncate">{item.label}</span>
                          )}
                          {narrow ? null : item.to.endsWith("/chat") && unread > 0 ? (
                            <span
                              className={cn(
                                "ml-auto grid min-w-5 shrink-0 place-items-center rounded-full px-1.5",
                                "bg-accent text-2xs font-bold text-white tabular-nums",
                              )}
                              aria-label={`${unread} unread`}
                            >
                              {unread > 99 ? "99+" : unread}
                            </span>
                          ) : item.countKey && (counts?.[item.countKey] ?? 0) > 0 ? (
                            // Amber, because amber is what "waiting on somebody"
                            // means everywhere else in the system; the accent is
                            // kept for a message somebody wrote to you.
                            <span
                              className={cn(
                                "ml-auto grid min-w-5 shrink-0 place-items-center rounded-full px-1.5",
                                "bg-ochre-50 text-2xs font-bold text-fg-waiting tabular-nums ring-1 ring-inset ring-ochre-100",
                              )}
                              aria-label={`${counts?.[item.countKey]} waiting`}
                            >
                              {(counts?.[item.countKey] ?? 0) > 99 ? "99+" : counts?.[item.countKey]}
                            </span>
                          ) : null}
                        </>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        );
      })}
    </nav>
  );
}

/** The heading over a section of the sidebar. Pressing it folds the section. */
/** Whether a section holds the given screen, or one beneath it. */
function holdsPath(group: NavGroup, pathname: string): boolean {
  return group.items.some(
    (item) =>
      pathname === item.to || (item.to !== "/admin" && pathname.startsWith(`${item.to}/`)),
  );
}

function SectionHeading({
  heading,
  open,
  waiting = 0,
  onToggle,
}: {
  heading: string;
  open: boolean;
  /** What is waiting inside, said on the heading while the section is shut. */
  waiting?: number;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="group flex h-7 w-full items-center gap-1 rounded-xs px-2 text-2xs font-semibold text-fg-subtle transition-colors hover:bg-rail-hover hover:text-fg"
    >
      <span className="flex-1 truncate text-left">{heading}</span>
      {!open && waiting > 0 ? (
        <span className="rounded-full bg-ochre-50 px-1.5 text-2xs font-bold tabular-nums text-fg-waiting ring-1 ring-inset ring-ochre-100">
          {waiting > 99 ? "99+" : waiting}
        </span>
      ) : null}
      {/* Always shown, pointing down when open and across when shut, so the
          state of every section can be read without hovering over it. */}
      <ChevronDown
        className={cn(
          "size-3.5 shrink-0 transition-transform duration-150",
          open ? "opacity-60 group-hover:opacity-100" : "-rotate-90",
        )}
        aria-hidden="true"
      />
    </button>
  );
}

/**
 * One pinned place.
 *
 * A row like the rows under it, with the screen's own icon where it has one.
 * The controls that move it or unpin it stay out of sight in the sidebar
 * until the pointer or the keyboard is on the row; in a phone's drawer,
 * where nothing hovers, they are always there. Folded to the strip there is
 * only room for the icon, so the name is in the title, as it is for every
 * other row.
 */
function FavouriteRow({
  favourite,
  icon: Icon,
  here,
  onRail,
  narrow,
  first,
  last,
  onNavigate,
  onRemove,
  onMove,
}: {
  favourite: Favourite;
  icon: LucideIcon;
  /** Whether this is the address that is open. */
  here: boolean;
  onRail: boolean;
  narrow: boolean;
  first: boolean;
  last: boolean;
  onNavigate?: () => void;
  onRemove: () => void;
  onMove: (by: -1 | 1) => void;
}) {
  const icon = (
    <Icon
      className={cn("size-4 shrink-0", here ? "text-accent" : "text-fg-subtle")}
      strokeWidth={here ? 2.25 : 1.75}
      aria-hidden="true"
    />
  );

  if (narrow) {
    return (
      <li>
        <Link
          to={favourite.to}
          onClick={onNavigate}
          title={favourite.label}
          aria-label={favourite.label}
          aria-current={here ? "page" : undefined}
          className={cn(
            "flex h-8 items-center justify-center rounded-xs transition-colors duration-100",
            here ? "bg-brand-50" : "hover:bg-rail-hover",
          )}
        >
          {icon}
        </Link>
      </li>
    );
  }

  return (
    <li
      className={cn(
        "group/pin flex items-center rounded-xs pr-1 transition-colors duration-100",
        here ? "bg-brand-50" : "hover:bg-rail-hover",
      )}
    >
      <Link
        to={favourite.to}
        onClick={onNavigate}
        aria-current={here ? "page" : undefined}
        className={cn(
          "flex min-w-0 flex-1 items-center gap-2.5 rounded-xs pl-2 text-base transition-colors duration-100",
          onRail ? "h-8" : "h-10",
          here ? "font-medium text-brand-700" : "text-fg-muted group-hover/pin:text-fg",
        )}
      >
        {icon}
        <span className="truncate">{favourite.label}</span>
      </Link>
      <span
        className={cn(
          "shrink-0 items-center",
          onRail ? "hidden group-focus-within/pin:flex group-hover/pin:flex" : "flex",
        )}
      >
        {first ? null : (
          <PinControl tip="Move up" label={`Move ${favourite.label} up`} onClick={() => onMove(-1)}>
            <ChevronUp className="size-3.5" />
          </PinControl>
        )}
        {last ? null : (
          <PinControl
            tip="Move down"
            label={`Move ${favourite.label} down`}
            onClick={() => onMove(1)}
          >
            <ChevronDown className="size-3.5" />
          </PinControl>
        )}
        <PinControl tip="Unpin" label={`Unpin ${favourite.label}`} onClick={onRemove}>
          <X className="size-3.5" />
        </PinControl>
      </span>
    </li>
  );
}

/** A control on a pinned row: smaller than an icon button, to fit three in a row's height. */
function PinControl({
  tip,
  label,
  onClick,
  children,
}: {
  /** What it does, in a word or two. */
  tip: string;
  /** The same, with the name of the place, for a screen reader. */
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Tooltip label={tip}>
      <button
        type="button"
        aria-label={label}
        onClick={onClick}
        className="grid size-6 shrink-0 place-items-center rounded-xs text-fg-subtle transition-colors duration-100 hover:bg-surface hover:text-fg"
      >
        <span aria-hidden="true">{children}</span>
      </button>
    </Tooltip>
  );
}
